<template>
  <div class="relative flex min-h-screen w-full bg-muted/30">
    <Sheet v-model:open="mobileOpen">
      <SheetContent side="left" class="w-[280px] p-0">
        <div class="flex h-full flex-col">
          <div class="flex h-16 items-center gap-3 border-b px-6">
            <div class="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
              <Sparkles class="size-5" />
            </div>
            <div>
              <p class="text-xs font-semibold uppercase tracking-wider text-muted-foreground">AlphaGo</p>
              <p class="text-sm font-semibold text-foreground">Dashboard</p>
            </div>
          </div>
          <nav class="flex-1 space-y-2 overflow-y-auto px-4 py-4">
            <SheetClose
              v-for="item in visibleNavigation"
              :key="item.to"
              as-child
            >
              <button
                type="button"
                :class="navButtonClass(item, false)"
                @click="navigate(item)"
              >
                <component
                  :is="item.icon"
                  :class="iconClass(item)"
                />
                <div class="text-left">
                  <span class="block text-sm font-semibold leading-tight">
                    {{ item.title }}
                  </span>
                  <span class="text-xs text-muted-foreground">
                    {{ item.description }}
                  </span>
                </div>
              </button>
            </SheetClose>
          </nav>
          <div class="border-t px-4 py-3">
            <SheetClose as-child>
              <button
                type="button"
                class="flex w-full items-center justify-between rounded-lg border border-destructive/20 px-4 py-2 text-sm font-semibold text-destructive transition hover:bg-destructive/10"
                @click="logout"
              >
                <span> Cerrar sesión </span>
                <LogOut class="size-4" />
              </button>
            </SheetClose>
          </div>
        </div>
      </SheetContent>
    </Sheet>

    <aside :class="sidebarClasses">
      <div class="flex h-16 items-center gap-3 px-4">
        <div class="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <Sparkles class="size-5" />
        </div>
        <div v-if="!sidebarCollapsed" class="flex flex-col">
          <span class="text-xs font-semibold uppercase tracking-[0.35em] text-muted-foreground">
            AlphaGo
          </span>
          <span class="text-lg font-semibold leading-tight text-foreground">
            Dashboard
          </span>
        </div>
      </div>
      <Separator />
      <nav class="flex-1 space-y-1 overflow-y-auto px-3 py-4">
        <button
          v-for="item in visibleNavigation"
          :key="item.to"
          type="button"
          :class="navButtonClass(item)"
          @click="navigate(item)"
        >
          <component
            :is="item.icon"
            :class="iconClass(item)"
          />
          <div
            v-if="!sidebarCollapsed"
            class="flex flex-col text-left"
          >
            <span class="text-sm font-semibold leading-tight tracking-wide">
              {{ item.title }}
            </span>
            <span class="text-xs text-muted-foreground">
              {{ item.description }}
            </span>
          </div>
        </button>
      </nav>
      <div class="mt-auto space-y-3 px-4 pb-5">
        <div
          v-if="!sidebarCollapsed"
          class="rounded-xl border border-border/80 bg-card/70 p-3 text-xs text-muted-foreground shadow-soft"
        >
          <p class="font-semibold text-foreground">
            Sesión segura
          </p>
          <p>
            Recuerda cerrar tu sesión si compartes este equipo.
          </p>
        </div>
        <Button
          variant="outline"
          class="w-full justify-start gap-2 text-sm font-semibold"
          @click="logout"
        >
          <LogOut class="size-4" />
          <span v-if="!sidebarCollapsed">
            Cerrar sesión
          </span>
        </Button>
      </div>
    </aside>

    <div class="flex flex-1 flex-col">
      <header
        class="sticky top-0 z-40 flex h-16 items-center gap-4 border-b bg-background/80 px-4 backdrop-blur supports-[backdrop-filter]:bg-background/60 md:px-8"
      >
        <div class="flex items-center gap-3">
          <Button
            variant="ghost"
            size="icon"
            class="lg:hidden"
            @click="mobileOpen = true"
          >
            <Menu class="size-5" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            class="hidden lg:inline-flex"
            @click="toggleSidebar"
          >
            <component
              :is="sidebarCollapsed ? PanelRightOpen : PanelLeft"
              class="size-5"
            />
          </Button>
          <div>
            <p class="text-xs uppercase tracking-[0.3em] text-muted-foreground">
              Panel de control
            </p>
            <h1 class="text-lg font-semibold leading-none text-foreground">
              {{ currentPageTitle }}
            </h1>
          </div>
        </div>

        <div class="ml-auto flex items-center gap-3">
          <div
            class="hidden w-64 items-center gap-2 rounded-full border border-border/80 bg-card px-3 py-1.5 text-sm text-muted-foreground shadow-soft md:flex"
          >
            <Search class="size-4 text-muted-foreground" />
            <input
              type="search"
              placeholder="Buscar en el panel..."
              class="w-full bg-transparent text-sm text-foreground placeholder:text-muted-foreground/70 focus:outline-none"
            />
          </div>
          <Button variant="ghost" size="icon" class="hidden md:inline-flex">
            <Bell class="size-5 text-muted-foreground" />
          </Button>

          <DropdownMenu>
            <DropdownMenuTrigger as-child>
              <button
                class="flex items-center gap-3 rounded-full border border-border/70 bg-card px-2 py-1.5 pr-3 text-left text-sm shadow-soft transition hover:border-border"
              >
                <Avatar class="size-9">
                  <AvatarImage :src="user?.avatar" />
                  <AvatarFallback>
                    {{ userInitials }}
                  </AvatarFallback>
                </Avatar>
                <div class="hidden min-w-[120px] max-w-[200px] flex-col md:flex">
                  <span class="truncate text-sm font-semibold leading-tight text-foreground">
                    {{ userName }}
                  </span>
                  <span class="truncate text-xs text-muted-foreground">
                    {{ userRoleLabel }}
                  </span>
                </div>
                <ChevronDown class="hidden size-4 text-muted-foreground md:block" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" class="w-60">
              <DropdownMenuLabel class="flex flex-col gap-0.5">
                <span class="text-sm font-semibold text-foreground">
                  {{ userName }}
                </span>
                <span class="text-xs text-muted-foreground">
                  {{ userEmail }}
                </span>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem @click="goToSettings">
                <Settings class="mr-2 size-4" />
                Preferencias
              </DropdownMenuItem>
              <DropdownMenuItem class="text-destructive focus:text-destructive" @click="logout">
                <LogOut class="mr-2 size-4" />
                Cerrar sesión
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main class="flex-1 overflow-y-auto">
        <div class="mx-auto flex w-full max-w-7xl flex-col gap-6 px-4 py-6 md:px-8 md:py-10">
          <!-- <div class="glass-surface rounded-2xl border border-border/60 p-5 shadow-soft">
            <div class="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p class="text-xs font-semibold uppercase tracking-[0.32em] text-muted-foreground">
                  Bienvenido de nuevo
                </p>
                <h2 class="mt-1 text-2xl font-semibold text-foreground">
                  {{ userName }}
                </h2>
              </div>
              <div class="flex items-center gap-2">
                <Badge variant="secondary" class="rounded-full border border-border px-4 py-1 text-xs uppercase tracking-wide">
                  {{ userRoleLabel }}
                </Badge>
                <Button variant="default" class="hidden gap-2 md:inline-flex" @click="goToSettings">
                  <Settings class="size-4" />
                  Ajustar panel
                </Button>
              </div>
            </div>
          </div> -->

          <router-view v-slot="{ Component }">
            <transition name="fade" mode="out-in">
              <component :is="Component" />
            </transition>
          </router-view>
        </div>
      </main>
    </div>
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  LayoutDashboard,
  Users,
  Megaphone,
  CalendarDays,
  Clapperboard,
  Podcast,
  Layers,
  Settings,
  Smartphone,
  Menu,
  LogOut,
  Bell,
  Search,
  ChevronDown,
  PanelLeft,
  PanelRightOpen,
  Sparkles,
  Package,
  Building2,
  Tag,
} from "lucide-vue-next";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetClose,
  SheetContent,
} from "@/components/ui/sheet";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";

const router = useRouter();
const route = useRoute();

const sidebarCollapsed = ref(false);
const mobileOpen = ref(false);
const user = ref(null);

const navigationItems = [
  {
    title: "Dashboard",
    description: "Resumen general",
    to: "/dashboard",
    icon: LayoutDashboard,
    roles: ["user", "superadmin", "admin", "guardia", "almacen"],
  },
  {
    title: "Usuarios",
    description: "Gestión de usuarios",
    to: "/users",
    icon: Users,
    roles: ["superadmin", "admin"],
  },
  {
    title: "Compañías",
    description: "Gestión de empresas",
    to: "/companies",
    icon: Building2,
    roles: ["superadmin", "admin"],
  },
  {
    title: "Publicidad",
    description: "Campañas activas",
    to: "/publicity",
    icon: Megaphone,
    roles: ["user", "superadmin", "admin"],
  },
  {
    title: "Productos",
    description: "Gestión de productos",
    to: "/products",
    icon: Package,
    roles: ["superadmin", "admin"],
  },
  {
    title: "Categorías de Productos",
    description: "Gestión de categorías",
    to: "/product-categories",
    icon: Tag,
    roles: ["superadmin", "admin"],
  },
  {
    title: "Eventos",
    description: "Calendario oficial",
    to: "/events",
    icon: CalendarDays,
    roles: ["user", "superadmin", "admin"],
  },
  {
    title: "Videos",
    description: "Biblioteca multimedia",
    to: "/videos",
    icon: Clapperboard,
    roles: ["user", "superadmin", "admin"],
  },
  {
    title: "Podcasts",
    description: "Audio y episodios",
    to: "/podcasts",
    icon: Podcast,
    roles: ["user", "superadmin", "admin"],
  },
  {
    title: "Grupos",
    description: "Segmentación de equipos",
    to: "/groups",
    icon: Layers,
    roles: ["superadmin", "admin"],
  },
  {
    title: "Categorías",
    description: "Organización de contenido",
    to: "/category",
    icon: Sparkles,
    roles: ["superadmin", "admin"],
  },
  {
    title: "Configuración",
    description: "Ajustes del sistema",
    to: "/configuration",
    icon: Settings,
    roles: ["superadmin", "admin"],
  },
  {
    title: "Móvil",
    description: "Experiencia móvil",
    to: "/interest",
    icon: Smartphone,
    roles: ["user", "superadmin", "admin"],
  },
];

const userRoles = computed(() => {
  const rawRoles = user.value?.rol;
  if (!rawRoles) return [];
  if (Array.isArray(rawRoles)) return rawRoles.map((role) => String(role).toLowerCase());
  if (typeof rawRoles === "string") {
    return rawRoles
      .split(",")
      .map((role) => role.trim().toLowerCase())
      .filter(Boolean);
  }
  return [];
});

const visibleNavigation = computed(() =>
  navigationItems.filter((item) => {
    if (!item.roles || item.roles.length === 0) return true;
    return item.roles.some((role) => userRoles.value.includes(role.toLowerCase()));
  }),
);

const currentPageTitle = computed(() => {
  const match = navigationItems.find((item) => route.path.startsWith(item.to));
  return match ? match.title : "Panel principal";
});

const sidebarClasses = computed(() =>
  cn(
    "hidden border-r border-border/80 bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/70 transition-all duration-300 lg:flex lg:flex-col",
    sidebarCollapsed.value ? "w-20" : "w-72",
  ),
);

const isActive = (item) => route.path.startsWith(item.to);

const iconClass = (item) =>
  cn(
    "size-5 shrink-0 transition-colors duration-200",
    isActive(item)
      ? "text-primary-foreground"
      : "text-muted-foreground group-hover:text-primary",
    sidebarCollapsed.value ? "" : "mt-0.5",
  );

const navButtonClass = (item, respectCollapse = true) =>
  cn(
    "group relative flex w-full items-center gap-3 rounded-xl px-3 py-2 text-sm font-semibold transition-all duration-200",
    isActive(item)
      ? "bg-primary text-primary-foreground shadow-soft"
      : "text-muted-foreground hover:bg-muted hover:text-foreground",
    respectCollapse && sidebarCollapsed.value ? "justify-center" : "justify-start",
  );

const userName = computed(
  () =>
    user.value?.name ||
    user.value?.nombre ||
    user.value?.username ||
    user.value?.usuario ||
    "Usuario",
);

const userEmail = computed(
  () =>
    user.value?.email ||
    user.value?.correo ||
    user.value?.mail ||
    user.value?.emailAddress ||
    "Cuenta sin correo",
);

const userRoleLabel = computed(() => {
  if (!userRoles.value.length) return "Rol no asignado";
  return userRoles.value
    .map((role) => role.charAt(0).toUpperCase() + role.slice(1))
    .join(" · ");
});

const userInitials = computed(() => {
  const name = userName.value;
  const parts = name.split(" ").filter(Boolean);
  if (!parts.length) return "US";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
});

const toggleSidebar = () => {
  sidebarCollapsed.value = !sidebarCollapsed.value;
};

const navigate = (item) => {
  router.push(item.to);
  mobileOpen.value = false;
};

const goToSettings = () => {
  router.push("/configuration");
};

const logout = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("user");
  router.push("/");
};

const hydrateUser = () => {
  try {
    const stored = localStorage.getItem("user");
    user.value = stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.warn("No se pudo convertir el usuario almacenado", error);
    user.value = null;
  }
};

const handleStorageChange = (event) => {
  if (event.key === "user" || event.key === "token") {
    hydrateUser();
  }
};

onMounted(() => {
  hydrateUser();
  window.addEventListener("storage", handleStorageChange);
});

onUnmounted(() => {
  window.removeEventListener("storage", handleStorageChange);
});
</script>
